import { BlockError } from '@/blocks.04-project/block-error/block-error';
import {LazyInit} from '@/blocks.04-project/lazy-init/lazy-init';
import toArray from 'lodash-es/toArray';
import debounce from 'lodash-es/debounce';
import map from 'lodash-es/map';
import find from 'lodash-es/find';
import forEach from 'lodash-es/forEach';

/**
 * Класс Якорей
 * @example
 *
 * Аттрибуты:
 * [data-scroll-offset ] {number, default = 24} - отступ до таргетов при скролле
 *
 * Классы:
 * .anchors__button.active - активный якорь
 *
 * Переменные:
 * --anchors-header-height - высота шапки якорей
 * --anchors-scroll-offset - отступ до заголовка при скролле
 */
class Anchors {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с классом anchors
   */
  constructor(elem) {
    this.BlockError = new BlockError({ blockName: 'Anchors', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.Anchors = this;
		this.block.AsyncAnchors = new Promise((resolve) => {
			this.promiseResolve = resolve;
		}).then((ins) => ins);

		this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'anchors_inited' });
		this.LazyInit.init();
  }

  init = async () => {
		this.scrollOffset = this.block.dataset['scrollOffset'] || 24;
		this.prevYPosition = 0;
		this.active = null;

		this.header = this.block.querySelector('.anchors__header');
		if (!this.header) return this.BlockError.error('Отсутствует .anchors__header');

		this.links = toArray(this.header.querySelectorAll('[href]'));
		this.targets = map(this.links, (link) => {
			const target = document.querySelector(link.hash);
			if (target) {
				target.link = link;
				return target;
			}
		});

		this.updateObserver();
		this.block.style.setProperty('--anchors-scroll-offset', this.scrollOffset + 'px');

		window.addEventListener('resize', this.updateObserver, { passive: true });

		this.promiseResolve(this);
		this.block.AsyncTabs = this;
  };

	updateObserver = debounce(() => {
		// Обновление облости слежения если что-либо поменялось
		if (this.windowHeight !== window.innerHeight || this.topMargin !== parseInt(window.getComputedStyle(this.header).top, 10)) {
			this.updateOffsets();
			this.initObserver();
		}
	}, 200);

	updateOffsets = () => {
		this.windowHeight = window.innerHeight;
		this.anchorHeaderHeight = this.header.clientHeight;

		this.topMargin = parseInt(window.getComputedStyle(this.header).top, 10);
		this.topOffset = this.scrollOffset + this.topMargin + this.anchorHeaderHeight;
		this.bottomOffset = Math.abs(this.windowHeight - this.topOffset - 10); // -10px область видимости обзервера после шапки

		this.block.style.setProperty('--anchors-header-height', this.anchorHeaderHeight + 'px');
	};

	initObserver = () => {
		if (this.observer) this.observer.disconnect();

		// Сильно ограничиваем область слежения, чтобы предотвратить попадания сразу нескольких элементов в область видимости
		this.observer = new IntersectionObserver(this.observerHandler, {
			rootMargin: `-${this.topOffset}px 0px -${this.bottomOffset}px 0px`,
			threshold: 0,
		});
		forEach(this.targets, (target) => this.observer.observe(target));
	};

	observerHandler = (entries) => {
		// Определение направления скролла для очистки
		const scrollTop = window.scrollY;
		if (scrollTop > this.prevYPosition) this.direction = 'down';
		else if (scrollTop < this.prevYPosition) this.direction = 'up';
		this.prevYPosition = scrollTop;

		// Активный элемент всегда самый первый видимый из элементов
		const visibleEntry = find(entries, {isIntersecting: true});
		if (visibleEntry) this.activate(visibleEntry.target);

		// Очистка при выходе из зоны видимости всех элементов
		if (this.direction === 'up' && entries.length === 1 && !entries[0].isIntersecting && entries[0].target === this.targets[0]) this.clear();
	};

	activate = debounce((target) => {
		this.active = target;
		this.clear();
		this.active.link.classList.add('active');
	}, 50);

	clear = () => forEach(this.links, (elem) => elem.classList.remove('active'));

  destroy = () => {
		window.removeEventListener('resize', this.updateObserver, { passive: true });
		if (this.observer) this.observer.disconnect();
    if (this.block) {
      this.block.classList.remove('anchors_inited');
      delete this.block.Anchors;
    }
  };

  static init() {
    Array.from(document.querySelectorAll('.anchors:not(.anchors_inited)')).forEach((elem) => new Anchors(elem));
  }
}

document.addEventListener('DOMContentLoaded', Anchors.init);

// Экспорт;
window.Block.Anchors = Anchors;
